import axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { isValid } from 'date-fns';

import { isDefined, isEmpty } from '@hsp/utilities';

import { customEvents } from 'constants/custom-events';
import { strings } from 'constants/index';
import { SET_AUTHORIZATION, TOKEN_EXPIRED } from 'services/http/constants/headers';
import { CancelError } from 'services/http/errors';
import { HttpHeaders } from 'services/http/http-headers';
import { InterceptorErrorCallback, RemoveInterceptor } from 'types/services/http/index';
import { publishCustomEvent } from 'utilities/events';

const onResponse = (response: AxiosResponse): AxiosResponse => {
  const { data, headers: responseHeaders, status } = response;
  const { headers } = new HttpHeaders(responseHeaders);
  const expired = headers.get(TOKEN_EXPIRED);
  const token = headers.get(SET_AUTHORIZATION);

  if (isDefined(token)) {
    publishCustomEvent(customEvents.newTokenEvent, { token });
  }

  if (isDefined(expired) && !isEmpty(expired) && isValid(expired)) {
    publishCustomEvent(customEvents.refreshToken);
  }

  if (status >= 200 || status <= 299 || status === 422) {
    return data;
  }

  throw response;
};

const onResponseError = (error: unknown, onError?: InterceptorErrorCallback): Promise<AxiosError> => {
  if (axios.isCancel(error)) {
    return Promise.reject(new CancelError());
  }

  const message = strings.Api.UnknownError;

  if (isEmpty(error)) {
    return Promise.reject(message);
  }

  if (typeof error === 'string') {
    return Promise.reject(error);
  }

  if (!axios.isAxiosError(error)) {
    return Promise.reject(message);
  }

  switch (error.response?.status) {
    case 403:

    case 511:
      publishCustomEvent(customEvents.logOffEvent);

      return Promise.reject('Token issue, redirecting to login page');
  }

  if (!isDefined(onError)) {
    return Promise.reject(message);
  }

  return onError(error);
};

export const addResponseInterceptor = (
  instance: AxiosInstance,
  onError?: InterceptorErrorCallback
): RemoveInterceptor => {
  const id = instance.interceptors.response.use(onResponse, (error) => onResponseError(error, onError));

  return () => instance.interceptors.response.eject(id);
};
